import moment from "moment";
import { ExpandableTable } from "../../../../components/UniversalTable";
import { defaultTableColumns } from "../../../../helpers";
import { toMoney } from "shared/utils";
import { PaystubItem } from "../../../../types/verified/paystub";
import { IncomeDetailsSlice } from "./slices/IncomeDetailsSlice";
import { PseudoTabs } from "./components/PseudoTabs";
import { DocumentCard } from "views/applications-id-report/slices/IncomeReportSlice/components/DocumentCard";
import { PaystubDetailsSlice } from "./slices/PaystubDetailsSlice";
import { EmployerInformationSlice } from "./slices/EmployerInformationSlice";
import { EmployeeInformationSlice } from "./slices/EmployeeInformationSlice";
import { SimpleTable } from "./components/SimpleTable";
import { useMemo } from "react";

export const PaystubTable = ({ income }: { income: PaystubItem[] }) => {
  const flatPaystubs = useMemo(
    () => income.flatMap((i) => i.data.document.income_sources),
    [income]
  );

  return (
    <ExpandableTable
      cellClassName="p-4 pl-0 text-sm self-center"
      headerRowClassName="text-content-300"
      renderExpanded={({ original }) => {
        return (
          <div className="px-6 py-8 bg-background-900">
            <IncomeDetailsSlice paystub={original} />

            {original.pay_stubs.map((ps, psIdx) => {
              const paystubName = "Paystub " + (psIdx + 1);

              const docScore =
                  ps.doc_analysis.form_analysis[0].form_authenticity.score,
                reason_codes =
                  ps.doc_analysis.form_analysis[0].form_authenticity
                    .reason_codes;

              return (
                <div key={ps.uuid}>
                  <div className="mt-6 mb-4">
                    <PseudoTabs title={paystubName} />
                  </div>

                  <DocumentCard
                    score={docScore}
                    asset={ps.asset?.asset}
                    reason_codes={reason_codes}
                    ignorePrint
                  />

                  <PaystubDetailsSlice paystub={ps} />
                  <EmployerInformationSlice paystub={ps} />
                  <EmployeeInformationSlice paystub={ps} />

                  {/* Pay distribution table */}
                  <SimpleTable
                    data={ps.pay_distribution}
                    columns={[
                      {
                        label: "Bank Name",
                        render: (d) => String(d.bank_name) || "-"
                      },
                      {
                        label: "Current pay",
                        render: (d) => toMoney(d.current_pay.cents / 100)
                      },
                      {
                        label: "Account",
                        render: (d) => String(d.account_number) || "-"
                      },
                      {
                        label: "Type",
                        render: (d) => d.bank_account_type || "-"
                      },
                      {
                        label: "Description",
                        render: (d) => d.description || "-"
                      }
                    ]}
                  />

                  {/* Earnings table */}
                  {/* Earning | Current pay | YTD pay | Cur. hrs | Description  */}
                  <SimpleTable
                    data={ps.earnings.totals}
                    columns={[
                      {
                        label: "Earning",
                        render: (e) => e.description || "-"
                      },
                      {
                        label: "Current pay",
                        render: (e) => toMoney(e.current_pay.cents / 100)
                      },
                      {
                        label: "YTD pay",
                        render: (e) => toMoney(e.ytd_pay.cents / 100)
                      },
                      {
                        label: "Current hours",
                        render: (e) => e.current_hours || "-"
                      },
                      {
                        label: "Description",
                        render: (e) => String(e.canonical_description) || "-"
                      }
                    ]}
                  />

                  {/* Deduction table  */}
                  {/* Deduction | Current pay */}
                  <SimpleTable
                    data={ps.deductions.totals}
                    columns={[
                      {
                        label: "Deduction",
                        render: (d) => d.description || "-",
                        className: "w-1/5"
                      },
                      {
                        label: "Current pay",
                        render: (d) => toMoney(d.current_pay.cents / 100)
                      }
                    ]}
                  />
                </div>
              );
            })}
          </div>
        );
      }}
      rows={flatPaystubs.map((item) => {
        return {
          source: item.employer || "",
          dateRange:
            moment(item.start_date).format("ll") + " - " + item.end_date
              ? moment(item.end_date).format("ll")
              : "Present",
          grossMonthly: toMoney(item.gross_monthly_income_amount),
          netMonthly: toMoney(item.net_monthly_income_amount),
          original: item
        };
      })}
      columns={defaultTableColumns}
    />
  );
};
